import { create } from 'zustand'

/**
 * Network reachability — "can this device reach the API at all?".
 *
 * This is NOT server health. A 5xx means the device is online and the server
 * answered badly; that is shown by the screen's own error card
 * (<QueryState>/<ErrorState>), never as "Connection lost" (UX-016). Only
 * failures where no response arrived (fetch TypeError, axios network error,
 * timeouts) and the browser's own `offline` event count here.
 */
export type HealthStatus = 'online' | 'offline' | 'unknown'

interface HealthState {
  status: HealthStatus
  lastSuccess: number | null
  consecutiveFailures: number
  lastFailure: number | null
  /** Mirror of navigator.onLine; false flips status to offline immediately. */
  browserOnline: boolean
  recordSuccess: () => void
  /** Record a request that got NO response (network failure, not a 5xx). */
  recordFailure: () => void
  setBrowserOnline: (online: boolean) => void
  setStatus: (status: HealthStatus) => void
  reset: () => void
}

// 2+ consecutive network failures OR >15s with no success → offline
const FAILURE_THRESHOLD = 2
const SUSTAINED_OUTAGE_MS = 15_000

function deriveStatus(
  prev: HealthStatus,
  consecutiveFailures: number,
  lastSuccess: number | null,
  lastFailure: number | null,
): HealthStatus {
  if (consecutiveFailures >= FAILURE_THRESHOLD) return 'offline'
  // Sustained-outage: had a known-good baseline, then no success for >15s
  if (
    lastFailure !== null &&
    lastSuccess !== null &&
    lastFailure - lastSuccess > SUSTAINED_OUTAGE_MS
  ) {
    return 'offline'
  }
  return prev
}

const initialBrowserOnline = typeof navigator === 'undefined' ? true : navigator.onLine !== false

export const useHealthStore = create<HealthState>((set) => ({
  status: initialBrowserOnline ? 'unknown' : 'offline',
  lastSuccess: null,
  consecutiveFailures: 0,
  lastFailure: null,
  browserOnline: initialBrowserOnline,
  recordSuccess: () => {
    set(() => ({
      lastSuccess: Date.now(),
      consecutiveFailures: 0,
      lastFailure: null,
      // A response proves the network works even if navigator.onLine lags.
      browserOnline: true,
      status: 'online',
    }))
  },
  recordFailure: () => {
    set((state) => {
      const lastFailure = Date.now()
      const consecutiveFailures = state.consecutiveFailures + 1
      return {
        consecutiveFailures,
        lastFailure,
        status: deriveStatus(state.status, consecutiveFailures, state.lastSuccess, lastFailure),
      }
    })
  },
  setBrowserOnline: (online) => {
    set((state) => ({
      browserOnline: online,
      // Going offline is trusted immediately; coming back online waits for a
      // real response (the poller pings on the `online` event).
      status: online ? state.status : 'offline',
    }))
  },
  setStatus: (status) => set({ status }),
  reset: () =>
    set({
      status: 'unknown',
      lastSuccess: null,
      consecutiveFailures: 0,
      lastFailure: null,
      browserOnline: true,
    }),
}))

export const HEALTH_CONSTANTS = {
  FAILURE_THRESHOLD,
  SUSTAINED_OUTAGE_MS,
}
